import React, { useState, useRef, useEffect } from 'react';
import { styled } from '@stitches/react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// 头部容器
const HeaderContainer = styled('header', {
  width: '100%', // 设置容器宽度为100%
  height: '70px', // 设置容器高度为70px
  display: 'flex', // 使用flex布局
  alignItems: 'center', // 垂直居中对齐
  justifyContent: 'center', // 居中对齐，内部容器控制布局
  background: 'var(--primary-color)', // 深蓝色背景色，与Logo背景一致
  color: '#FFFFFF', // 文字颜色为白色
  position: 'sticky', // 使用粘性定位
  top: 0, // 距离顶部0px
  zIndex: 100, // 设置层级为100
  opacity: '0.7' // 设置透明度为0.7，增加透明度
});

// 头部内容容器 - 限制最大宽度与页面内容一致
const HeaderContent = styled('div', {
  width: '100%', // 宽度100%
  maxWidth: 'var(--container-width)', // 最大宽度与页面内容一致（1200px）
  display: 'flex', // 使用flex布局
  alignItems: 'center', // 垂直居中对齐
  justifyContent: 'space-between', // 两端对齐：Logo在左，钱包在右
  padding: '0 32px', // 左右内边距32px
  gap: '40px', // 子元素之间的间距为40px

  '@media (max-width: 1200px)': { // 在容器最大宽度以下时
    padding: '0 32px' // 保持左右内边距
  }
});

// Logo容器
const LogoContainer = styled(Link, {
  display: 'flex', // 使用flex布局
  alignItems: 'center', // 垂直居中对齐
  gap: '12px', // 子元素之间的间距为12px
  textDecoration: 'none', // 移除文本装饰
  color: '#FFFFFF', // 文字颜色为白色
  fontWeight: 'bold', // 文字加粗
  fontSize: '24px', // 文字大小为24px

  '& img': { // Logo图片样式
    height: '50px', // 图片高度50px
    width: 'auto', // 宽度自适应
    maxWidth: '200px', // 最大宽度200px
    objectFit: 'contain', // 保持图片比例

    '@media (max-width: 768px)': { // 移动端适配
      height: '40px', // 移动端高度40px
      maxWidth: '150px' // 移动端最大宽度150px
    }
  },

  '& > div:last-child': { // Logo文字容器
    display: 'flex',
    flexDirection: 'column',

    '& > div:first-child': { // BITENGINE文字样式
      fontSize: '24px',
      fontWeight: 'bold',
      color: '#FFFFFF',
      lineHeight: '1.2',

      '@media (max-width: 768px)': {
        fontSize: '20px'
      }
    }
  }
});

// 标语文字已移除 - 当前未使用

// 右侧工具栏
const RightToolbar = styled('div', {
  display: 'flex', // 使用flex布局
  alignItems: 'center', // 垂直居中对齐
  gap: '16px', // 子元素之间的间距为16px

  '@media (max-width: 768px)': { // 在移动端设备上
    gap: '8px' // 子元素间距减小到8px
  }
});

// 登录按钮
const LoginButton = styled(Link, {
  padding: '8px 24px', // 内边距：上下8px，左右24px
  background: '#FFD166', // 背景色为黄色
  color: '#1E293B', // 文字颜色为深色
  fontWeight: 'bold', // 文字加粗
  borderRadius: '4px', // 圆角4px
  textDecoration: 'none', // 移除文本装饰
  fontSize: '16px', // 文字大小为16px
  whiteSpace: 'nowrap', // 文本不换行

  '&:hover': { // 鼠标悬停时
    background: '#FFE57F' // 背景色变为浅黄色
  },

  '@media (max-width: 768px)': {
    padding: '6px 12px',
    fontSize: '14px',
    minWidth: '60px',
    maxWidth: '120px',
    textAlign: 'center',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  }
});

// 语言切换器
const LanguageToggle = styled('div', {
  display: 'flex', // 使用flex布局
  alignItems: 'center', // 垂直居中对齐
  justifyContent: 'center', // 水平居中对齐
  width: '36px', // 宽度36px (从40px调整)
  height: '36px', // 高度36px (从40px调整)
  borderRadius: '50%', // 圆形
  color: '#FFFFFF', // 文字颜色为白色
  marginLeft: '16px', // 左边距16px
  cursor: 'pointer', // 鼠标指针为手型
  position: 'relative', // 相对定位
  fontSize: '18px', // 添加字体大小

  '@media (max-width: 768px)': { // 在移动端设备上
    width: '32px', // 宽度减小到32px (从36px调整)
    height: '32px', // 高度减小到32px (从36px调整)
    marginLeft: '8px', // 左边距减小到8px
    fontSize: '16px' // 移动端字体大小
  }
});

// 语言下拉菜单
const LanguageDropdown = styled('div', {
  position: 'absolute', // 绝对定位
  top: '40px', // 距离顶部40px (从45px调整)
  right: '0', // 距离右侧0px
  background: '#FFFFFF', // 背景色为白色
  boxShadow: '0 4px 12px rgba(0,0,0,0.15)', // 阴影效果
  borderRadius: '4px', // 圆角4px
  padding: '8px 0', // 上下内边距8px
  zIndex: 150, // 层级为150
  minWidth: '160px', // 最小宽度160px
  display: 'none', // 默认隐藏

  variants: {
    isOpen: {
      true: {
        display: 'block' // 当isOpen为true时显示
      }
    }
  }
});

// 语言选项
const LanguageOption = styled('div', {
  padding: '8px 16px', // 内边距：上下8px，左右16px
  fontSize: '14px', // 文字大小为14px
  color: '#1E293B', // 文字颜色为深色
  cursor: 'pointer', // 鼠标指针为手型
  display: 'flex', // 使用flex布局
  alignItems: 'center', // 垂直居中对齐
  gap: '8px', // 子元素之间的间距为8px

  '&:hover': { // 鼠标悬停时
    background: '#F5F5F5' // 背景色变为浅灰色
  },

  variants: {
    isActive: {
      true: {
        fontWeight: 'bold', // 文字加粗
        color: '#FFD166' // 文字颜色变为黄色
      }
    }
  }
});

// 汉堡菜单图标
const HamburgerIcon = styled('div', {
  display: 'none', // 默认隐藏
  cursor: 'pointer', // 鼠标指针为手型
  fontSize: '20px', // 文字大小为20px (从24px调整)
  marginLeft: '16px', // 左边距16px

  '@media (max-width: 768px)': { // 在移动端设备上
    display: 'none', // 移动端也隐藏
    fontSize: '18px' // 移动端字体大小
  }
});



/**
 * 网站头部组件
 * 显示Logo、主导航和登录按钮
 */
const Header: React.FC = () => {
  const { t, i18n } = useTranslation(); // 添加t函数用于翻译
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [languageDropdownOpen, setLanguageDropdownOpen] = useState(false);
  const languageToggleRef = useRef<HTMLDivElement>(null);

  // 可用语言选项
  const languages = [
    { code: 'en', name: 'English', flag: '🇺🇸' },
    { code: 'zh', name: '中文', flag: '🇨🇳' },
    { code: 'ru', name: 'Русский', flag: '🇷🇺' },
    { code: 'ur', name: 'اردو', flag: '🇵🇰' },
    { code: 'ko', name: '한국어', flag: '🇰🇷' }
  ];

  // 移动菜单切换
  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };



  // 语言下拉菜单切换
  const toggleLanguageDropdown = (e: React.MouseEvent) => {
    e.stopPropagation();
    setLanguageDropdownOpen(!languageDropdownOpen);
  };

  // 切换语言
  const changeLanguage = (langCode: string) => {
    i18n.changeLanguage(langCode);
    localStorage.setItem('language', langCode); // 保存语言设置到localStorage
    setLanguageDropdownOpen(false);
  };

  // 点击外部区域关闭语言下拉菜单
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (languageToggleRef.current && !languageToggleRef.current.contains(event.target as Node)) {
        setLanguageDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <>
      <HeaderContainer>
        <HeaderContent>
          <LogoContainer to="/">
            <img src="/images/bitengine logo.png" alt="BITENGINE" />
            <div>BITENGINE</div>
          </LogoContainer>
          {/* <NavMenu>
            <NavLink to="/">Home</NavLink>
            <NavLink to="/products">Products</NavLink>
            <NavLink to="/ai">AI Mining</NavLink>
            <NavLink to="/about">About</NavLink>
            <NavLink to="/news">Blog</NavLink>
            <NavLink to="/help">Help</NavLink>
            <NavLink to="/contact">Contacts</NavLink>
          </NavMenu> */}

          <RightToolbar>
            <LoginButton as="a" href="https://nft.bitengine.org/dashboard/" target="_blank" rel="noopener noreferrer">{t('header.connectWallet')}</LoginButton>
            <LanguageToggle ref={languageToggleRef} onClick={toggleLanguageDropdown}>
              🌐
              <LanguageDropdown isOpen={languageDropdownOpen}>
                {languages.map((lang) => (
                  <LanguageOption
                    key={lang.code}
                    isActive={i18n.language === lang.code}
                    onClick={() => changeLanguage(lang.code)}
                  >
                    <span>{lang.flag}</span> {lang.name}
                  </LanguageOption>
                ))}
              </LanguageDropdown>
            </LanguageToggle>
            <HamburgerIcon onClick={toggleMobileMenu}>☰</HamburgerIcon>
          </RightToolbar>
        </HeaderContent>
      </HeaderContainer>

      {/* 移动端菜单已暂时隐藏 */}
      {/* <MenuOverlay isOpen={mobileMenuOpen} onClick={closeMobileMenu} />
      
      <MobileMenu isOpen={mobileMenuOpen}>
        <CloseButton onClick={closeMobileMenu}>✕</CloseButton>
        <MobileMenuLogo>BITENGINE</MobileMenuLogo>
        
        <MobileNavLink to="/" onClick={closeMobileMenu}>Home</MobileNavLink>
        
        <SocialMediaContainer>
          <SocialIcon>
            <span role="img" aria-label="telegram">✈️</span>
          </SocialIcon>
          <SocialIcon>
            <span role="img" aria-label="twitter">𝕏</span>
          </SocialIcon>
          <SocialIcon>
            <span role="img" aria-label="youtube">▶️</span>
          </SocialIcon>
        </SocialMediaContainer>
        
        <MobileFooterInfo>
          Mining at your fingertips<br/>
          BeMine © 2018—2025
        </MobileFooterInfo>
      </MobileMenu> */}
    </>
  );
};

export default Header; 